import { useCountdown } from '@/hooks/useCountdown';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CountdownTimerProps {
  endsAt: number;
  /**
   * `inline` — compact inline display used inside auction cards.
   * `block`  — large block display used on the auction detail page.
   */
  variant?: 'inline' | 'block';
}

// ─── Shared segment renderers ─────────────────────────────────────────────────

function InlineSegment({
  val,
  label,
  accent,
}: {
  val: number;
  label: string;
  accent: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-epic text-xl font-bold tabular-nums leading-none ${accent}`}>
        {String(val).padStart(2, '0')}
      </span>
      <span className="text-[9px] tracking-[0.25em] uppercase text-iron-500 mt-0.5">
        {label}
      </span>
    </div>
  );
}

function BlockSegment({
  val,
  label,
  accent,
}: {
  val: number;
  label: string;
  accent: string;
}) {
  return (
    <div className="flex flex-col items-center bg-iron-950 border border-iron-800 px-5 py-3 min-w-[64px]">
      <span className={`font-epic text-3xl font-bold tabular-nums ${accent}`}>
        {String(val).padStart(2, '0')}
      </span>
      <span className="text-[9px] tracking-[0.25em] uppercase text-iron-500 mt-1">
        {label}
      </span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Unified countdown timer component.
 *
 * - `variant="inline"` — compact horizontal timer for auction list cards.
 * - `variant="block"` — large boxed timer for the auction detail hero section.
 */
export function CountdownTimer({
  endsAt,
  variant = 'inline',
}: CountdownTimerProps) {
  const { hours, minutes, seconds, isUrgent, isDead } = useCountdown(endsAt);
  const accentClass = isUrgent ? 'text-primary' : 'text-secondary';
  const sepClass = isUrgent ? 'text-primary/60' : 'text-secondary/40';

  if (isDead) {
    if (variant === 'block') {
      return (
        <div className="bg-iron-950 border border-iron-800 px-6 py-4 text-center">
          <span className="text-iron-500 text-sm tracking-widest uppercase font-bold font-epic">
            Auction Ended
          </span>
        </div>
      );
    }
    return (
      <span className="text-iron-500 text-xs tracking-widest uppercase font-bold">
        Ended
      </span>
    );
  }

  if (variant === 'block') {
    const sep = (
      <span className={`font-epic text-2xl pb-4 ${sepClass}`}>:</span>
    );
    return (
      <div className="flex items-center gap-1">
        <BlockSegment val={hours} label="Hours" accent={accentClass} />
        {sep}
        <BlockSegment val={minutes} label="Min" accent={accentClass} />
        {sep}
        <BlockSegment val={seconds} label="Sec" accent={accentClass} />
      </div>
    );
  }

  // inline
  const sep = (
    <span className={`font-epic text-lg pb-2 ${sepClass}`}>:</span>
  );
  return (
    <div className="flex items-end gap-2">
      <InlineSegment val={hours} label="hrs" accent={accentClass} />
      {sep}
      <InlineSegment val={minutes} label="min" accent={accentClass} />
      {sep}
      <InlineSegment val={seconds} label="sec" accent={accentClass} />
    </div>
  );
}
